import React, { useCallback, useEffect, useRef } from "react";
import "./styles/DesktopWindow.css";
import { UserStoryDto } from "../../dto/UserStoryDto";

interface FinderWindowProps {
  stories: UserStoryDto[];
  selectedId?: number;
  onSelect: (id: number) => void;
}

const FinderWindow = ({ stories, selectedId, onSelect }: FinderWindowProps) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const fileListRef = useRef<HTMLDivElement>(null);

  const isScrollingProgrammatically = useRef(false);

  const findCenteredCover = useCallback(() => {
    const container = wrapRef.current;
    if (!container || isScrollingProgrammatically.current) return;

    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestDistance = Infinity;
    let closestStoryId: number | null = null;

    const figures = container.querySelectorAll<HTMLElement>("figure");
    figures.forEach((figure, index) => {
      const figureRect = figure.getBoundingClientRect();
      const figureCenter = figureRect.left + figureRect.width / 2;
      const distance = Math.abs(containerCenter - figureCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestStoryId = stories[index]?.id || null;
      }
    });

    if (closestStoryId && closestStoryId !== selectedId) {
      onSelect(closestStoryId);
    }
  }, [stories, selectedId, onSelect]);

  useEffect(() => {
    const container = wrapRef.current;
    if (!container) return;
    const sel = container.querySelector<HTMLElement>("figure.selected");
    if (sel) {
      isScrollingProgrammatically.current = true;
      sel.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });

      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 1000);
    }
  }, [selectedId]);

  useEffect(() => {
    const fileListContainer = fileListRef.current;
    if (!fileListContainer || !selectedId) return;

    const selectedRow =
      fileListContainer.querySelector<HTMLElement>("tr.selected");
    if (selectedRow) {
      selectedRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedId]);

  useEffect(() => {
    const container = wrapRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        findCenteredCover();
      }, 100);
    };

    container.addEventListener("scroll", handleScroll);
    setTimeout(findCenteredCover, 100);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [findCenteredCover]);

  const handleCoverClick = (userStoryId: number) => {
    onSelect(userStoryId);
  };

  return (
    <div className="osx-desktop-window">
      {/* Title bar */}
      <div className="osx-desktop-window-titlebar">
        <div className="osx-desktop-window-dots">
          <span className="dot red" />
          <span className="dot yellow" />
          <span className="dot green" />
        </div>
        <div className="osx-desktop-window-title">
          <div className="osx-desktop-window-title-content">
            Visitor Stories
          </div>
        </div>
      </div>

      <div className="osx-desktop-window-content">
        {/* Coverflow + file list */}
        <div className="finder-mainpane">
          <section className="wrap" ref={wrapRef}>
            <div className="move">
              {stories.map((userStory) => (
                <figure
                  key={userStory.id}
                  className={userStory.id === selectedId ? "selected" : ""}
                  onClick={() => handleCoverClick(userStory.id)}
                >
                  <img
                    draggable={false}
                    src={userStory.visualUrl}
                    alt={userStory.titleEN}
                  />
                </figure>
              ))}
            </div>
          </section>

          <div className="file-list" ref={fileListRef}>
            <table>
              <thead>
                <tr>
                  <th align="left">English</th>
                  <th align="left">Deutsch</th>
                </tr>
              </thead>
              <tbody>
                {stories.map((userStory) => (
                  <tr
                    key={userStory.id}
                    className={userStory.id === selectedId ? "selected" : ""}
                  >
                    <td>{userStory.titleEN}</td>
                    <td>{userStory.titleDE}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="osx-desktop-window-footer">
        {stories.length} visitor stories available
      </div>
    </div>
  );
};

export default FinderWindow;
